import React, { useEffect, useRef, useCallback, useState } from "react";
import { getVentilatorAlarmHistory } from "../../../helper/alarm-helper";
import priority_high_svg from "../../../common/icons/svg/priority-high.svg";
import priority_medium_svg from "../../../common/icons/svg/priority-medium.svg";
import priority_low_svg from "../../../common/icons/svg/priority-low.svg";
import { covertDateString, covertTimeString } from "../../../common/utils/CommonUtils";
import { SET_PARAMETERS, MANEUVERS } from "../../../common/ENUMS/PARAMETER_ENUM";
import { MODE } from "../../../common/ENUMS/VENTILATOR_MODES_ENUM";
import moment from "moment";
import filter from "../../../common/images/filter.png";
import { ALARM_TYPE_FILTER, ALARM_PRIORITY_FILTER, USER_VIEW } from "../../../common/constant/common-constants";
import lung_svg from "../../../common/icons/svg/lungs.svg";
import consolidation_svg from "../../../common/icons/svg/consolidation.svg"
import { convertUTCtoIST } from "../../../common/utils/CommonUtils";
import VentilatorAlarmTableDesktopView from "./VentilatorAlarmTableDesktopView";
import VentilatorAlarmTableMobileView from "./VentilatorAlarmTableMobileView";

export function VentilatorAlarmTable(props) {
    const [data, setData] = useState([]);
    const [activeAlarm, setActiveAlarm] = useState([]);
    const [endOffPage, setEndOffPage] = useState(false);
    const [createdAt, setCreatedAt] = useState(null);
    const [offset, setOffset] = useState(null);
    const isFetchingRef = useRef(false);
    const tableRef = useRef(null);

    const [filterDropdown, setFilterDropdown] = useState({ open: false, column: null });
    const [selectedTypeFilter, setSelectedTypeFilter] = useState(ALARM_TYPE_FILTER?.[0]?.name);
    const [selectedPriorityFilter, setPrioritySelectedFilter] = useState(ALARM_PRIORITY_FILTER?.[0]?.name);
    const [isLoading, setIsLoading] = useState(false);
    const dropdownRef = useRef(null);

    const resetState = () => {
        setEndOffPage(false);
        setData([]);
        setCreatedAt(null);
        setOffset(null);
    }

    const getVentilatorAlarm = (created, offs, selectedTypeFilter, selectedPriorityFilter) => {

        if (endOffPage) return;
        // console.log("selectedTypeFilter in API:", selectedTypeFilter)
        setIsLoading(true);
        getVentilatorAlarmHistory({
            bedId: props.bedId,
            typefilter: selectedTypeFilter,
            priorityfilter: selectedPriorityFilter,
            createdAt: created ? created?.toString() : null,
            limit: 100,
            offset: offs ? offs : null,
        })
            .then((res) => {
                if (res?.ventilatorAlarms?.length < 100) setEndOffPage(true);
                const ventilatorAlarms = res.ventilatorAlarms;
                if (ventilatorAlarms) {
                    if (!created) setCreatedAt(ventilatorAlarms?.[0]?.createdAt);
                    setData((prevData) => {
                        setOffset(prevData.length + ventilatorAlarms.length);
                        return [...prevData, ...ventilatorAlarms];
                    });
                }
            })
            .finally(() => {
                isFetchingRef.current = false;
                setIsLoading(false);
            })
            .catch((err) => {
                console.log("alarm err: ", err);
            });
    };

    const handleScroll = useCallback(() => {
        const table = tableRef.current;
        if (table) {
            const scrollPosition = table.scrollTop + table.clientHeight;
            const isLastRows = scrollPosition >= table.scrollHeight - table.clientHeight * 0.1;
            // console.log("isLastRows", isLastRows, "isFetchingRef.current", isFetchingRef.current);
            if (isLastRows && !isFetchingRef.current && offset) {
                isFetchingRef.current = true;
                getVentilatorAlarm(createdAt, offset, selectedTypeFilter, selectedPriorityFilter);
            }
        }
    }, [offset]);

    useEffect(() => {
        const table = tableRef.current;
        if (table) {
            table.addEventListener('scroll', handleScroll);
        }

        return () => {
            // Cleanup: Remove the event listener when the component is unmounted
            if (table) {
                table.removeEventListener('scroll', handleScroll);
            }
        };
    }, [handleScroll]);


    useEffect(() => {
        if (!offset) {
            getVentilatorAlarm(null, null, selectedTypeFilter, selectedPriorityFilter);
        }
    }, [offset])

    useEffect(() => {
        let activeAl = [];
        props.ventilatorAlarmData?.forEach(e => {
            activeAl.push({
                category: null,
                createdAt: null,
                message: e.name,
                observedTime: moment(e.time, "YYYYMMDDHHmmss").format("YYYY-MM-DD HH:mm:ss"),
                priority: e.priority,
                resolvedTime: null,
                subcategory: null,
                type: e.type,
                status: 'active'
            })
        });
        setActiveAlarm(activeAl);
    }, [props.ventilatorAlarmData]);
    // console.log("props.ventilatorAlarmData live", props.ventilatorAlarmData)
    useEffect(() => {
        resetState();
    }, [props.bedId, selectedTypeFilter, selectedPriorityFilter]);

    const toggleFilterDropdown = (column) => {
        setFilterDropdown((prev) => ({
            open: !(prev.open && prev.column === column),
            column,
        }));
    };


    const handleFilterSelect = (filter, data) => {
        if (data === "TYPE") {
            setSelectedTypeFilter(filter.name);
            setPrioritySelectedFilter(ALARM_PRIORITY_FILTER?.[0]?.name)
        }
        if (data === "PRIORITY") {
            setPrioritySelectedFilter(filter.name)
        };

        if (data === "MOBILE-VIEW-FILTER") {
            //Write noe mobile view only has type filter
            setSelectedTypeFilter(filter.name);
            setPrioritySelectedFilter(ALARM_PRIORITY_FILTER?.[0]?.name)

        }
        setFilterDropdown({ open: false, column: null });
    }

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                setFilterDropdown({ open: false, column: null });
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);


    const getPriority = (priority) => {
        let pr = priority?.toUpperCase();
        switch (pr) {
            case "HIGH":
                return { icon: priority_high_svg, color: '#AD0606', textColor: '#fff' };
            case "MEDIUM":
                return { icon: priority_medium_svg, color: '#FCC331', textColor: '#000' };
            case "LOW":
                return { icon: priority_low_svg, color: '#3ADAE5', textColor: '#000' };
            default:
                return { icon: '', color: '#3ADAE5' };
        }
    }

    let livealarms = [...activeAlarm].sort((a, b) => new Date(b.observedTime) - new Date(a.observedTime))
    let sortedEvents = (selectedTypeFilter === 'ACTIVE') ? [...livealarms] : [...livealarms, ...data] || [];


    return (
        <>
            {props.userView === USER_VIEW.DESKTOP && (
                <VentilatorAlarmTableDesktopView
                    tableRef={tableRef}
                    header={props.header}
                    sortedEvents={sortedEvents}
                    selectedTypeFilter={selectedTypeFilter}
                    selectedPriorityFilter={selectedPriorityFilter}
                    toggleFilterDropdown={toggleFilterDropdown}
                    filterDropdown={filterDropdown}
                    dropdownRef={dropdownRef}
                    handleFilterSelect={handleFilterSelect}
                    getPriority={getPriority}
                    isLoading={isLoading}
                />
            )}

            {props.userView === USER_VIEW.MOBILE && (
                <VentilatorAlarmTableMobileView
                    tableRef={tableRef}
                    header={props.header}
                    sortedEvents={sortedEvents}
                    dropdownRef={dropdownRef}
                    getPriority={getPriority}
                    isLoading={isLoading}
                    userView={props.userView}
                    selectedTypeFilter={selectedTypeFilter}
                    handleFilterSelect={handleFilterSelect}
                />
            )}
        </>
    );




}